import { useCallback, useEffect, useRef, useState } from 'react';

import { useAccess } from '../../../auth/AccessContext.jsx';
import { BADGE_PAGE, awaitingViewer, badgeText } from './runs.js';

/**
 * The Fund Register tab's badge (design 2026-10-02 section 8): the NAV runs
 * waiting for THIS person - the CHECKED runs they may approve, when they hold
 * core.price.approve; their own runs left out, since four eyes would refuse
 * them (runs.js awaitingViewer). One page is read (BADGE_PAGE); "+" says there
 * are more.
 *
 * Read when the tab bar mounts and again whenever Unit Prices says a run was
 * decided (reload). A failed read shows no badge: the screen itself says what
 * went wrong when it is opened.
 *
 * @returns {{ text: string|null, reload: () => void }}
 */
export function useRunsBadge() {
    const { api, canView, canAct, principal } = useAccess();
    const canApprove = canAct('unitPrices', 'approve');
    const shown = canView('unitPrices') && canApprove;
    const uid = principal?.uid ?? null;
    const [text, setText] = useState(null);
    const latest = useRef(0);

    const reload = useCallback(async () => {
        const ask = ++latest.current;
        if (!shown) {
            setText(null);
            return;
        }
        try {
            const checked = await api.pricing.runs({ state: 'CHECKED', limit: BADGE_PAGE });
            if (ask !== latest.current) return;
            setText(badgeText(awaitingViewer({
                checked: checked.runs ?? [],
                moreChecked: Boolean(checked.nextCursor)
            }, { uid, canApprove })));
        } catch {
            if (ask === latest.current) setText(null);
        }
    }, [api, shown, canApprove, uid]);

    useEffect(() => {
        reload();
    }, [reload]);

    return { text, reload };
}
